import { Reducer } from 'redux';
import { IDataState, DataActionTypes } from './types';
import { API_CALLS } from '../../utilities/constants';
import { ConfigActionTypes } from '../config/types';
import { DashboardActionTypes } from '../dashboard/types';
import { IAgent } from 'types';
import { StudentActionTypes } from '../student/types';
import { UploadFileStatus } from '../../types';
import { ApplyActionTypes } from '../apply/types';

const initialState: IDataState = {
  isConfigLoaded: false,
  isUniConfigLoaded: false,
  isStudentConfigLoaded: false,
  isStudentLoaded: false,
  isDashboardConfigLoaded: false,
  isDashboardStudentsLoaded: false,
  isDocsLoaded: false,
  agentSuggestions: [],
  apiCallsInProgress: [],
  downloadFile: null,
  downloadError: null,
  isWsPaused: false,
  uploadError: null,
  uploadFile: null,
};

const handleButtonTriggerEvent = (state: IDataState, eventType: string, sourceComponentId: string) => {
  const apiCall = state.apiCallsInProgress.find(x => x.callName === eventType);

  if (!apiCall) {
    return {
      ...state,
      apiCallsInProgress: [
        ...state.apiCallsInProgress,
        {
          callName: eventType,
          retryNumber: 0,
          timeToNextCall: 0,
          hasFailed: false,
          componentIds: [sourceComponentId],
        },
      ],
    };
  }
  return {
    ...state,
    apiCallsInProgress: [
      ...state.apiCallsInProgress,
      {
        callName: eventType,
        retryNumber: 0,
        timeToNextCall: 0,
        hasFailed: false,
        componentIds: [...apiCall.componentIds, sourceComponentId],
      },
    ],
  };
};

const handleButtonResponseEvent = (state: IDataState, eventType: string, sourceComponentId: string) => {
  const apiCall = state.apiCallsInProgress.find(x => x.callName === eventType);

  const newApiComponentCallsInProgress = (apiCall?.componentIds ?? []).filter(x => x !== sourceComponentId);

  if (newApiComponentCallsInProgress.length === 0) {
    return {
      ...state,
      apiCallsInProgress: state.apiCallsInProgress.filter(x => x.callName !== eventType),
    };
  }
  const newState = { ...state };
  const newApiCall = newState.apiCallsInProgress.find(x => x.callName === eventType);
  if (newApiCall) {
    newApiCall.componentIds = newApiComponentCallsInProgress;
    return {
      ...newState,
    };
  }
  return state;
};

const reducer: Reducer<IDataState> = (state = initialState, action: any): IDataState => {
  switch (action.type) {
    case ConfigActionTypes.GET_UNI_CONFIG:
      return {
        ...state,
        isConfigLoaded: false,
      };
    case ConfigActionTypes.SET_UNI_CONFIG_LOADED:
      return {
        ...state,
        isUniConfigLoaded: action.payload,
      };
    case ConfigActionTypes.SET_STUDENT_CONFIG_LOADED:
      return {
        ...state,
        isStudentConfigLoaded: action.payload,
      };

    case ConfigActionTypes.GET_UNI_CONFIG_SUCCESS:
    case ConfigActionTypes.GET_UNI_CONFIG_FAILURE:
      const isStudentApp = window.location.pathname.startsWith('/student');
      return {
        ...state,
        isConfigLoaded: true,
        isStudentConfigLoaded: isStudentApp,
        isUniConfigLoaded: !isStudentApp,
      };

    case DashboardActionTypes.GET_STUDENTS_SUCCESS: {
      return {
        ...state,
        isDashboardStudentsLoaded: true,
      };
    }

    case DataActionTypes.GET_AGENTS: {
      const isInProgressAlready = state.apiCallsInProgress.find(x => x.callName === API_CALLS.GET_AGENTS) !== undefined;

      if (!isInProgressAlready) {
        return {
          ...state,
          apiCallsInProgress: [
            ...state.apiCallsInProgress,
            {
              callName: API_CALLS.GET_AGENTS,
              retryNumber: 0,
              timeToNextCall: 0,
              hasFailed: false,
            },
          ],
        };
      }
      return state;
    }
    case DataActionTypes.GET_AGENTS_SUCCESS: {
      const results =
        action.payload && action.payload.length
          ? action.payload.map((item: IAgent) => {
              return { ...item, id: item.s_key };
            })
          : [];
      return {
        ...state,
        agentSuggestions: results,
        apiCallsInProgress: state.apiCallsInProgress.filter(x => x.callName !== API_CALLS.GET_AGENTS),
      };
    }
    case DataActionTypes.GET_AGENTS_FAILURE: {
      return {
        ...state,
        agentSuggestions: [],
        apiCallsInProgress: state.apiCallsInProgress.filter(x => x.callName !== API_CALLS.GET_AGENTS),
      };
    }

    case StudentActionTypes.PASS_RECORDED_INTERVIEW:
    case StudentActionTypes.PASS_SCHEDULED_INTERVIEW: {
      const { sourceComponentId } = action.payload;
      return handleButtonTriggerEvent(state, API_CALLS.FINGER_PASS, sourceComponentId);
    }

    case StudentActionTypes.FAIL_RECORDED_INTERVIEW:
    case StudentActionTypes.FAIL_SCHEDULED_INTERVIEW: {
      const { sourceComponentId, rescind } = action.payload;
      return handleButtonTriggerEvent(
        state,
        rescind ? API_CALLS.FINGER_RESCIND_FAIL : API_CALLS.FINGER_FAIL,
        sourceComponentId,
      );
    }

    case StudentActionTypes.RESIT_RECORDED_INTERVIEW:
    case StudentActionTypes.RESIT_SCHEDULED_INTERVIEW: {
      const { rescind, sourceComponentId } = action.payload;
      return handleButtonTriggerEvent(
        state,
        rescind ? API_CALLS.FINGER_RESCIND_RESIT : API_CALLS.FINGER_RESIT,
        sourceComponentId,
      );
    }

    case StudentActionTypes.TOGGLE_RECORDED_INTERVIEW_SUCCESS:
    case StudentActionTypes.TOGGLE_RECORDED_INTERVIEW_FAILURE:
    case StudentActionTypes.TOGGLE_SCHEDULED_INTERVIEW_SUCCESS:
    case StudentActionTypes.TOGGLE_SCHEDULED_INTERVIEW_FAILURE: {
      const { sourceComponentId, eventType } = action.payload;
      return handleButtonResponseEvent(state, eventType, sourceComponentId);
    }

    case ConfigActionTypes.GET_UNI_CONFIG_BY_STUDENT_ID_SUCCESS:
    case ConfigActionTypes.GET_UNI_CONFIG_BY_STUDENT_ID_FAILURE:
      return {
        ...state,
        isConfigLoaded: true,
      };

    case ApplyActionTypes.GET_APPLICATION_RECORD:
      return {
        ...state,
        isStudentLoaded: false,
      };

    case ApplyActionTypes.GET_APPLICATION_RECORD_SUCCESS:
    case StudentActionTypes.GET_STUDENT_RECORD_SUCCESS:
      return {
        ...state,
        isStudentLoaded: true,
      };

    case StudentActionTypes.OPEN_STUDENT:
      return {
        ...state,
        isConfigLoaded: false,
        isStudentLoaded: false,
      };

    case DataActionTypes.GET_UPLOAD_FILE_URL: {
      const { fileName, fileIndex } = action.payload;
      return {
        ...state,
        uploadFile: {
          name: fileName,
          status: UploadFileStatus.UPLOADING,
          index: fileIndex,
          isFileUploading: true,
        },
        uploadError: null,
      };
    }
    case DataActionTypes.GET_UPLOAD_FILE_URL_FAILURE: {
      const { error, fileData } = action.payload;
      const uploadFile = fileData ? fileData : state.uploadFile;
      return {
        ...state,
        uploadFile: {
          ...uploadFile,
          status: UploadFileStatus.ERROR,
          isFileUploading: false,
        },
        uploadError: error,
      };
    }
    case DataActionTypes.UPLOAD_FILE_SUCCESS: {
      return {
        ...state,
        uploadFile: {
          ...state.uploadFile,
          status: UploadFileStatus.OK,
          isFileUploading: false,
        },
        uploadError: null,
      };
    }
    case DataActionTypes.UPLOAD_FILE_FAILURE: {
      const { error } = action.payload;
      return {
        ...state,
        uploadFile: {
          ...state.uploadFile,
          status: UploadFileStatus.ERROR,
          isFileUploading: false,
        },
        uploadError: error,
      };
    }

    default:
      return state;
  }
};

export { reducer as dataReducer };
